import { FieldErrors, UseFormRegister } from 'react-hook-form';
import { ConcentrationFormData } from 'interfaces/concentration';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import RadioInput from 'components/UI/RadioInput/RadioInput';

/**
 * @description Props for the InfusionComplete component
 * @typedef {Object} InfusioncompleteProps
 * @property {UseFormRegister<ConcentrationFormData>} register - React Hook Form register function
 * @property {FieldErrors<ConcentrationFormData>} errors - Form validation errors from React Hook Form
 * @property {boolean} canManage - Whether the user has permission to manage this section
 */
type InfusioncompleteProps = {
  register: UseFormRegister<ConcentrationFormData>;
  errors: FieldErrors<ConcentrationFormData>;
  canManage: boolean;
};

/**
 * @description Component for selecting infusion complete behavior (KVO rate or maintain infusion rate).
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2, SAD-003-4.3
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {InfusioncompleteProps} props - Component props
 *
 * @returns {JSX.Element} Rendered component
 */
const InfusionComplete: FC<InfusioncompleteProps> = ({
  register,
  errors,
  canManage,
}) => {
  const { t } = useTranslation();

  return (
    <div className="customRow position-relative pt-2">
      {!canManage && (
        <div className="device-concentration--disabled-item"></div>
      )}

      <div className="device-concentration-primary-secondary2--width">
        {t('device.infusion_complete')}
      </div>
      <div>
        <div className="customRow ps-0 align-basic-radios">
          <RadioInput
            {...register('infusion_complete', {
              required: true,
            })}
            data-testid="infusion_complete"
            value={'run_kvo_rate'}
            id="run-kvo-rate"
            title={t('device.run_kvo_rate')}
          />
          <RadioInput
            {...register('infusion_complete', {
              required: true,
            })}
            data-testid="infusion_complete"
            value={'maintain_infusion_rate'}
            id="maintain-infusion-rate"
            title={t('device.maintain_infusion_rate')}
          />
        </div>

        {errors?.infusion_complete?.type === 'required' && (
          <div
            className="invalid-feedback"
            data-testid="infusion_complete__required"
          >
            {t('device.errors.one_option__required')}
          </div>
        )}
      </div>
    </div>
  );
};

export default InfusionComplete;
